/* eslint-disable camelcase */
import React from 'react';
import {format} from 'date-fns';
import {
  Caption,
  Stack,
  TextContainer,
  TextStyle,
  Tooltip,
} from '@shopify/polaris';
import {withI18n, WithI18nProps, I18n} from '@shopify/react-i18n';
import {StarRating} from 'components/polaris/react/star-rating';
import {Heading, Paragraph} from '../typography';
import {Money, Rating, Review, Service} from 'sections/types';
import {formatCurrency} from 'utilities/numbers';
import {DATE_FORMATS} from 'global-constants';
import i18n from './locales';

import styles from './ReviewListing.scss';

/* tslint:disable */
// eslint-disable-next-line no-warning-comments, line-comment-position
const JSXStarRating: any = StarRating; // todo delete me after refactoring to TS
/* tslint:enable */

interface OwnProps {
  review: Review;
  currentServiceHandle: string;
}

type CombinedProps = WithI18nProps & OwnProps;

function ReviewListing({review, currentServiceHandle, i18n}: CombinedProps) {
  const {
    body,
    averageRating,
    ratings,
    shopName,
    reviewedAt,
    price,
    services,
  } = review;
  const jobPriceTitle = i18n.translate('Review.jobPrice');

  return (
    <article className={styles.ReviewListing}>
      <Stack spacing="tight" vertical>
        <Header title={shopName} reviewedAt={reviewedAt} />
        <div className={styles.RatingAndPriceContainer}>
          <Stack spacing="loose">
            <Stack.Item>
              <ReviewRating
                averageRating={averageRating}
                ratings={ratings}
                i18n={i18n}
              />
            </Stack.Item>
            <Stack.Item>
              <JobPrice price={price} title={jobPriceTitle} />
            </Stack.Item>
          </Stack>
        </div>
      </Stack>
      <TextContainer spacing="loose">
        <Testimonial testimonial={body} />
        {services.length > 1 ? (
          <OtherServices
            i18n={i18n}
            currentServiceHandle={currentServiceHandle}
            services={services}
          />
        ) : null}
      </TextContainer>
    </article>
  );
}

interface HeadProps {
  title: string;
  reviewedAt: string | null;
}

function Header({title, reviewedAt}: HeadProps) {
  return (
    <TextContainer spacing="tight">
      <Heading element="h3">{title}</Heading>
      {reviewedAt && (
        <Paragraph>
          <TextStyle variation="subdued">{reviewDate({reviewedAt})}</TextStyle>
        </Paragraph>
      )}
    </TextContainer>
  );
}

function JobPrice({price, title}: {price: Money | null; title: string}) {
  return price ? (
    <>
      <Paragraph element="h4">{title}</Paragraph>
      <Paragraph>
        <span className={styles.Amount}>
          <TextStyle variation="strong">
            {formatCurrency(price.amount, 0, price.currencyCode)}
          </TextStyle>
        </span>
      </Paragraph>
    </>
  ) : null;
}

interface RatingProps {
  averageRating: number;
  ratings: Rating[];
  i18n: I18n;
}

function ReviewRating({averageRating, ratings, i18n}: RatingProps) {
  return (
    <>
      <Paragraph element="h4">{i18n.translate('Review.rating')}</Paragraph>
      <span className={styles.AverageRating}>{averageRating}</span>
      <div className={styles.StarContainer}>
        {/*
        // @ts-ignore */}
        <Tooltip active={false} content={<RatingTooltips ratings={ratings} />}>
          <JSXStarRating value={averageRating} size={20} readOnly />
        </Tooltip>
      </div>
    </>
  );
}

interface ReviewRatingTooltipsProps {
  ratings: Rating[];
}

function RatingTooltips({ratings}: ReviewRatingTooltipsProps) {
  return ratings.map(rating => (
    <div key={rating.question.handle} className={styles.RatingTooltip}>
      <Caption>{rating.question.title}</Caption>
      <JSXStarRating value={rating.score} readOnly />
    </div>
  ));
}

function Testimonial({testimonial}: {testimonial: string}) {
  if (!testimonial) {
    return null;
  }

  return (
    <div className={styles.Testimonial}>
      <Paragraph>{testimonial.trim()}</Paragraph>
    </div>
  );
}

function reviewDate({reviewedAt}: {reviewedAt: string}) {
  return format(reviewedAt, DATE_FORMATS.POLARIS_SHORT);
}

function OtherServices({
  services,
  currentServiceHandle,
  i18n,
}: {
  services: Service[];
  currentServiceHandle: string;
  i18n: I18n;
}) {
  return (
    <Paragraph>
      <TextStyle variation="subdued">
        {i18n.translate('Review.alsoFor', {
          services: services
            .filter(service => service.handle !== currentServiceHandle)
            .map(service => service.shortLabel)
            .join(', '),
        })}
      </TextStyle>
    </Paragraph>
  );
}

export default withI18n(i18n)(ReviewListing);
